import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, List, Store, Building2, Wrench, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'issue_type', label: 'Issue Types', icon: AlertTriangle },
  { key: 'retailer', label: 'Retailers', icon: Store },
  { key: 'store', label: 'Stores', icon: Building2 },
  { key: 'agency', label: '3P Agencies', icon: Building2 },
  { key: 'request_type', label: 'Request Types', icon: Wrench },
];

function MasterTab({ type }) {
  const [items, setItems] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [name, setName] = useState('');

  const load = useCallback(() => {
    api.get(`/masters/${type}`, { params: { active_only: 'false' } }).then(r => setItems(r.data.items || []));
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editItem) {
        await api.put(`/masters/${type}/${editItem.master_id}`, { name });
        toast.success('Updated');
      } else {
        await api.post(`/masters/${type}`, { name });
        toast.success('Created');
      }
      setDlgOpen(false);
      setEditItem(null);
      setName('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (item) => {
    await api.put(`/masters/${type}/${item.master_id}`, { active: !item.active });
    toast.success(item.active ? 'Deactivated' : 'Activated');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item permanently?')) return;
    try {
      await api.delete(`/masters/${type}/${id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (item) => { setEditItem(item); setName(item.name); setDlgOpen(true); };
  const openCreate = () => { setEditItem(null); setName(''); setDlgOpen(true); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} items</p>
        <Button size="sm" onClick={openCreate} data-testid={`create-${type}-btn`}><Plus size={14} className="mr-1" /> Add</Button>
      </div>
      <div className="rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Name</TableHead><TableHead className="w-24">Status</TableHead><TableHead className="w-24">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">No items. Click "Add" to create one.</TableCell></TableRow>}
            {items.map(item => (
              <TableRow key={item.master_id} className="hover:bg-muted/50" data-testid={`master-row-${item.master_id}`}>
                <TableCell className="font-medium">{item.name}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className={`text-xs cursor-pointer ${item.active ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`} onClick={() => toggleActive(item)}>
                    {item.active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-${item.master_id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del(item.master_id)} data-testid={`delete-${item.master_id}`}><Trash2 size={13} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="sm:max-w-md" data-testid={`master-${type}-dialog`}>
          <DialogHeader><DialogTitle className="font-heading">{editItem ? 'Edit' : 'Add'} Item</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Name *</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Enter name..." data-testid={`master-${type}-name-input`} onKeyDown={e => e.key === 'Enter' && name.trim() && save()} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button><Button onClick={save} disabled={!name.trim()} data-testid={`master-${type}-save-btn`}>{editItem ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MastersPage() {
  return (
    <div data-testid="masters-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Master Data</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage centralized dropdown lists used across modules</p>
      </div>
      <Tabs defaultValue="issue_type">
        <TabsList className="flex-wrap h-auto gap-1">
          {TABS.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
              <t.icon size={14} className="mr-1.5" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map(t => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <MasterTab type={t.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
